import { createErrorFactory } from '@quotientjs/error';

/**
 * Formats for API key validation
 */ // Key Prefixes
const PUBLIC_KEY_PREFIX = "pk_";
// Key Lengths
const MIN_API_KEY_LENGTH = 20 // Minimum characters after prefix
;
const MAX_API_KEY_LENGTH = 40 // Maximum characters after prefix
;
// Validation Patterns
const PUBLIC_KEY_PATTERN = new RegExp(`^${PUBLIC_KEY_PREFIX}[a-zA-Z0-9]{${MIN_API_KEY_LENGTH},${MAX_API_KEY_LENGTH}}$`);
/**
 * Validates that a public key has the proper format
 */ function isValidPublicApiKeyFormat(key) {
    return PUBLIC_KEY_PATTERN.test(key);
}
function isPublicApiKey(key) {
    return isValidPublicApiKeyFormat(key);
}

const DEFAULT_API_URL = "https://www.getquotient.ai";
const ApiClientError = createErrorFactory({
    InvalidEndpoint: [
        "Invalid endpoint format: {endpoint}",
        {
            endpoint: String
        }
    ],
    InvalidApiKey: "Invalid API key",
    InvalidApiKeyType: "Invalid API key type",
    InvalidApiUrl: [
        "Invalid API URL: {url}",
        {
            url: String
        }
    ],
    ApiResponseNotJson: "API response is not JSON",
    ApiError: [
        "{message}",
        {
            message: String,
            status: Number,
            data: Object
        }
    ]
});
/**
 * Base client for making API requests
 * This is a clean base class with no environment-specific logic
 */ class ApiClient {
    /**
   * NOTE (amiller68): this is async to accommodate for browser-specific
   *  storage providers that may need to fetch values asynchronously.
   *  This shouldn't really be a big deal for non-async storage providers + server-side
   *  since it's called within the call method, which is already async.
   *
   * Get headers for requests
   * Override this method in subclasses to add custom headers
   */ async getHeaders() {
        return {
            "Content-Type": "application/json"
        };
    }
    /**
   * Generic method to execute any API request
   */ async call(request) {
        const { method, endpoint, params, body, auth, options } = request;
        const { beacon } = options || {};
        // check that the endpoint is the form of '/<path>'
        if (endpoint.charAt(0) !== "/") {
            throw ApiClientError.InvalidEndpoint({
                endpoint
            });
        }
        // Build the URL with query parameters
        let url = `${this.baseUrl}api/v0${endpoint}`;
        if (params) {
            const searchParams = new URLSearchParams();
            Object.keys(params).forEach((key)=>{
                const value = params[key];
                if (value !== undefined) {
                    // Handle arrays by appending multiple entries with the same key
                    if (Array.isArray(value)) {
                        value.forEach((item)=>{
                            searchParams.append(key, String(item));
                        });
                    } else {
                        searchParams.append(key, String(value));
                    }
                }
            });
            const queryString = searchParams.toString();
            if (queryString) {
                url += `?${queryString}`;
            }
        }
        // Get headers
        const headers = await this.getHeaders();
        // Add authorization header if API key is available
        if (this.apiKey && auth.required) {
            headers["Authorization"] = `Bearer ${this.apiKey}`;
        }
        // Configure fetch options
        const fetchOptions = {
            method,
            cache: "no-store",
            headers,
            ...beacon ? {
                keepalive: true
            } : {},
            // Include body for non-GET requests
            ...method !== "GET" && body ? {
                body: JSON.stringify(body)
            } : {}
        };
        // If this request should use beacon
        if (beacon) {
            // fire and forget
            fetch(url, fetchOptions);
            return true; // Type assertion needed for conditional return type
        }
        // Execute the request with regular fetch if not using beacon or beacon failed
        const response = await fetch(url, fetchOptions);
        // Parse the response
        let data;
        try {
            data = await response.json();
        } catch (error) {
            throw ApiClientError.ApiResponseNotJson({});
        }
        // Handle error responses
        if (!response.ok) {
            const errorMessage = data.error || data.message || "Unknown API error";
            throw ApiClientError.ApiError({
                message: errorMessage,
                status: response.status,
                data: data
            });
        }
        return data;
    }
    /**
   * Create a new API client
   */ constructor(options){
        const supportedKeyTypes = options.supportedKeyTypes || [
            "public",
            "private"
        ];
        const keyType = isPublicApiKey(options.apiKey) ? "public" : "private";
        if (!supportedKeyTypes.includes(keyType)) {
            throw ApiClientError.InvalidApiKeyType({});
        }
        try {
            this.baseUrl = new URL(options.baseUrl || DEFAULT_API_URL);
        } catch (error) {
            var _options_baseUrl;
            const strUrl = ((_options_baseUrl = options.baseUrl) == null ? void 0 : _options_baseUrl.toString()) || DEFAULT_API_URL;
            throw ApiClientError.InvalidApiUrl({
                url: strUrl
            });
        }
        this.apiKey = options.apiKey;
    }
}

/**
 * Base abstract class for all API requests
 */ class ApiRequest {
}

/**
 * Tracks an analytics event
 */ class TrackEventRequest extends ApiRequest {
    constructor(options, requestOptions){
        super(), this.method = "POST", this.endpoint = "/analytics", // Authentication requirements
        this.auth = {
            required: true
        }, // By default, use beacon for analytics events and don't wait for response
        this.options = {
            beacon: true
        };
        this.body = options;
        // Allow overriding the default options
        if (requestOptions) {
            this.options = {
                ...this.options,
                ...requestOptions
            };
        }
    }
}

class WhoAmIRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/whoami", // Authentication requirements
        this.auth = {
            required: true
        };
    }
}

/**
 * Upsert a person with their email subscription status
 */ class UpsertPersonRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/people", // Authentication requirements
        this.auth = {
            required: true
        };
        this.body = {
            emailAddress: options.emailAddress,
            emailSubscriptionStatus: options.emailSubscriptionStatus,
            firstName: options.firstName,
            lastName: options.lastName,
            jobTitle: options.jobTitle,
            leadScore: options.leadScore,
            lists: options.lists,
            events: options.events,
            properties: options.properties
        };
    }
}

/**
 * Upsert a company by domain.
 *
 * The `domain` field is required and acts as the upsert key within a business.
 * If multiple companies share the same domain (legacy CRM data), the oldest one
 * (by id) is updated.
 */ class UpsertCompanyRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/companies", this.auth = {
            required: true
        };
        this.body = {
            domain: options.domain,
            name: options.name,
            description: options.description,
            industries: options.industries,
            revenue: options.revenue,
            revenueCurrency: options.revenueCurrency,
            totalEmployees: options.totalEmployees,
            address1: options.address1,
            address2: options.address2,
            city: options.city,
            regionCode: options.regionCode,
            country: options.country,
            zip: options.zip,
            socialLinkFacebook: options.socialLinkFacebook,
            socialLinkInstagram: options.socialLinkInstagram,
            socialLinkTwitter: options.socialLinkTwitter,
            socialLinkLinkedIn: options.socialLinkLinkedIn,
            socialLinkAngelList: options.socialLinkAngelList,
            properties: options.properties
        };
    }
}

class UpsertListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/lists", this.auth = {
            required: true
        };
        this.body = {
            name: options.name,
            slug: options.slug,
            description: options.description
        };
    }
}

class ListListsRequest extends ApiRequest {
    constructor(options = {}){
        super(), this.method = "GET", this.endpoint = "/audience/lists", this.auth = {
            required: true
        };
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit
        };
    }
}

class GetListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/audience/lists/:slug", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.slug}`;
    }
}

class ListPeopleInListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/audience/lists/:slug/people", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.listSlug}/people`;
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit
        };
    }
}

class AddPeopleToListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/audience/lists/:slug/people", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.listSlug}/people`;
        this.body = {
            people: options.people
        };
    }
}

class RemovePeopleFromListRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "DELETE", this.endpoint = "/audience/lists/:slug/people", this.auth = {
            required: true
        };
        this.endpoint = `/audience/lists/${options.listSlug}/people`;
        this.body = {
            people: options.people
        };
    }
}

class GetBlogRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/blog/{slug}", this.auth = {
            required: true
        };
        this.endpoint = this.endpoint.replace("{slug}", options.slug);
        if (options.rawHtml) {
            this.params = {
                rawHtml: options.rawHtml
            };
        }
    }
}

class ListBlogsRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/blog/list", this.auth = {
            required: true
        };
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit,
            authorIds: options.authorIds,
            tagIds: options.tagIds,
            statuses: options.statuses
        };
    }
}

class ListAuthorsRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/blog/authors", this.auth = {
            required: true
        };
        this.params = {
            search: options.search,
            page: options.page,
            limit: options.limit
        };
    }
}

class ListTagsRequest extends ApiRequest {
    constructor(){
        super(), this.method = "GET", this.endpoint = "/blog/tags", this.auth = {
            required: true
        };
    }
}

class TriggerFlowRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/flow/{flowId}/trigger", this.auth = {
            required: true
        };
        this.endpoint = this.endpoint.replace("{flowId}", options.flowId);
        this.body = {
            personId: options.personId
        };
    }
}

class LsMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
        this.params = {
            ls: true,
            ...options.deep ? {
                deep: true
            } : {}
        };
    }
}

class CatMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "GET", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
    }
}

class WriteMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
        this.body = {
            title: options.title,
            content: options.content,
            tags: options.tags,
            pinned: options.pinned
        };
    }
}

class MkdirMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
        this.body = {
            name: options.name
        };
    }
}

class RmMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "DELETE", this.endpoint = "/memory", this.auth = {
            required: true
        };
        const cleanPath = options.path.startsWith("/") ? options.path.slice(1) : options.path;
        this.endpoint = cleanPath ? `/memory/${cleanPath}` : "/memory";
    }
}

class SearchMemoryRequest extends ApiRequest {
    constructor(options){
        super(), this.method = "POST", this.endpoint = "/memory/search", this.auth = {
            required: true
        };
        this.body = {
            query: options.query,
            ...options.tags && options.tags.length > 0 ? {
                tags: options.tags
            } : {}
        };
    }
}

export { AddPeopleToListRequest, ApiClient, ApiClientError, CatMemoryRequest, GetBlogRequest, GetListRequest, ListAuthorsRequest, ListBlogsRequest, ListListsRequest, ListPeopleInListRequest, ListTagsRequest, LsMemoryRequest, MkdirMemoryRequest, RemovePeopleFromListRequest, RmMemoryRequest, SearchMemoryRequest, TrackEventRequest, TriggerFlowRequest, UpsertCompanyRequest, UpsertListRequest, UpsertPersonRequest, WhoAmIRequest, WriteMemoryRequest };
